/**
 * Builds the head metadata for a public route: title, description, canonical,
 * hreflang alternates, and the Open Graph and Twitter tags that go with them.
 *
 * One generator for every page, driven by the route registry, so a page's
 * social card can never describe something other than the page, and every URL
 * it emits is absolute and on the apex domain.
 *
 * Kept free of `import.meta` and `@/` imports like the registry itself: the
 * Vite build config reaches this module through `public-routes`.
 */

import { SITE_URL, type Locale, type PublicPageType, type PublicRoute } from './public-routes';

/**
 * Marks a head element the SEO hooks created, so they can tell their own output
 * apart from the tags index.html ships — including in a prerendered snapshot,
 * which captures the head after the hooks have run.
 */
export const MANAGED_ATTRIBUTE = 'data-seo-managed';

/** Keeps a page out of the index and stops crawlers following its links. */
export const NOINDEX_CONTENT = 'noindex, nofollow';

/** Shown as the site name on social cards. */
const SITE_NAME = 'DebtDrone';

const SOCIAL_IMAGE = `${SITE_URL}/debtdrone-logo.png`;
const SOCIAL_IMAGE_ALT = 'The DebtDrone logo';

/**
 * A square logo, not a wide banner: `summary` renders it honestly, where
 * `summary_large_image` would promise artwork the site does not have.
 */
const TWITTER_CARD = 'summary';

const OPEN_GRAPH_LOCALES: Record<Locale, string> = {
  en: 'en_US',
  es: 'es_ES',
  de: 'de_DE',
};

/** Only dated, authored content is an `article`; everything else is a page. */
function openGraphType(pageType: PublicPageType): 'article' | 'website' {
  return pageType === 'blog-post' ? 'article' : 'website';
}

export interface MetaTag {
  /** Twitter and the description use `name`; Open Graph uses `property`. */
  key: 'name' | 'property';
  value: string;
  content: string;
}

export interface AlternateLink {
  hreflang: string;
  href: string;
}

export interface PageMetadata {
  title: string;
  description: string;
  /**
   * Absolute, apex-domain canonical for this page. Omitted for the not-found
   * page, which has no URL of its own: one file answers every unknown path.
   */
  canonical?: string;
  /** Absolute hreflang alternates. Empty when the page has no translations. */
  alternates: AlternateLink[];
  meta: MetaTag[];
}

function absolute(path: string): string {
  return `${SITE_URL}${path}`;
}

/** The tags every page carries, whatever its type. */
function socialTags(options: {
  title: string;
  description: string;
  locale: Locale;
  openGraphType: 'article' | 'website';
  canonical?: string;
}): MetaTag[] {
  const { title, description } = options;

  return [
    { key: 'name', value: 'description', content: description },
    { key: 'property', value: 'og:type', content: options.openGraphType },
    { key: 'property', value: 'og:site_name', content: SITE_NAME },
    { key: 'property', value: 'og:title', content: title },
    { key: 'property', value: 'og:description', content: description },
    ...(options.canonical
      ? [{ key: 'property' as const, value: 'og:url', content: options.canonical }]
      : []),
    { key: 'property', value: 'og:image', content: SOCIAL_IMAGE },
    { key: 'property', value: 'og:image:alt', content: SOCIAL_IMAGE_ALT },
    { key: 'property', value: 'og:locale', content: OPEN_GRAPH_LOCALES[options.locale] },
  ];
}

/** The Twitter card, which mirrors the Open Graph values. */
function twitterTags(title: string, description: string): MetaTag[] {
  return [
    { key: 'name', value: 'twitter:card', content: TWITTER_CARD },
    { key: 'name', value: 'twitter:title', content: title },
    { key: 'name', value: 'twitter:description', content: description },
    { key: 'name', value: 'twitter:image', content: SOCIAL_IMAGE },
    { key: 'name', value: 'twitter:image:alt', content: SOCIAL_IMAGE_ALT },
  ];
}

function buildMeta(route: PublicRoute, canonical: string): MetaTag[] {
  const { title, description } = route.metadata;

  const tags: MetaTag[] = socialTags({
    title,
    description,
    locale: route.locale,
    openGraphType: openGraphType(route.pageType),
    canonical,
  });

  // The other languages this exact page is published in.
  for (const alternate of route.alternates) {
    const locale = OPEN_GRAPH_LOCALES[alternate.hreflang as Locale];
    if (!locale || alternate.hreflang === route.locale) continue;
    tags.push({ key: 'property', value: 'og:locale:alternate', content: locale });
  }

  if (route.publishedAt) {
    tags.push({ key: 'property', value: 'article:published_time', content: route.publishedAt });
  }

  tags.push(...twitterTags(title, description));

  return tags;
}

const NOT_FOUND_TITLE = 'Page not found – DebtDrone';
const NOT_FOUND_DESCRIPTION =
  'That page does not exist. Browse the DebtDrone product, pricing, documentation, blog, and changelog instead.';

/**
 * Metadata for the not-found page.
 *
 * No canonical and no `og:url`: one file answers every unknown URL, so there is
 * no single address this page speaks for. It is served with a 404 and carries
 * `noindex` from `useNoindex`, so it never competes for a place in the index.
 */
export const NOT_FOUND_METADATA: PageMetadata = {
  title: NOT_FOUND_TITLE,
  description: NOT_FOUND_DESCRIPTION,
  alternates: [],
  meta: [
    ...socialTags({
      title: NOT_FOUND_TITLE,
      description: NOT_FOUND_DESCRIPTION,
      locale: 'en',
      openGraphType: 'website',
    }),
    ...twitterTags(NOT_FOUND_TITLE, NOT_FOUND_DESCRIPTION),
  ],
};

/** A registry route always has a canonical; only the not-found page does not. */
export interface RouteMetadataResult extends PageMetadata {
  canonical: string;
}

const METADATA_CACHE = new WeakMap<PublicRoute, RouteMetadataResult>();

/**
 * The head metadata for a route. Cached per route so callers hand `usePageSEO`
 * a stable object across renders.
 */
export function pageMetadata(route: PublicRoute): RouteMetadataResult {
  const cached = METADATA_CACHE.get(route);
  if (cached) return cached;

  const canonical = absolute(route.canonicalPath);
  const metadata: RouteMetadataResult = {
    title: route.metadata.title,
    description: route.metadata.description,
    canonical,
    alternates: route.alternates.map(({ hreflang, path }) => ({ hreflang, href: absolute(path) })),
    meta: buildMeta(route, canonical),
  };

  METADATA_CACHE.set(route, metadata);
  return metadata;
}
